import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Brain, Target, TrendingUp, AlertTriangle, CheckCircle,
  ChevronRight, ChevronLeft, BookOpen, ArrowLeft, Loader2,
  Award, RefreshCw, Star, Clock, Home, Lock, PenLine
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SkillAssessment = ({ onBack, studentClass = "6" }) => {
  const [subjects] = useState(['Mathematics', 'Science', 'English']);
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noChaptersMessage, setNoChaptersMessage] = useState('');

  // Test State
  const [view, setView] = useState('chapters');
  const [testDifficulty, setTestDifficulty] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    loadChapters();
  }, [selectedSubject, studentClass]);

  // Timer
  useEffect(() => {
    if (view === 'test' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            submitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [view, timeLeft]);

  const loadChapters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/skill-assessment/available-chapters/${studentClass}/${selectedSubject}`);
      setChapters(response.data.chapters || []);
      setNoChaptersMessage(response.data.message || '');
    } catch (error) {
      setChapters([]);
      setNoChaptersMessage('Unable to load chapters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canAttemptDifficulty = (chapter, difficulty) => {
    const progress = chapter.student_progress;
    if (difficulty === 'basic') return true;
    if (difficulty === 'medium') return progress.basic_score != null && progress.basic_score >= 50;
    if (difficulty === 'hard') return progress.medium_score != null && progress.medium_score >= 50;
    return false;
  };

  const getChapterStatus = (chapter) => {
    const p = chapter.student_progress;
    if (p.hard_score != null && p.hard_score >= 70) return 'mastered';
    if (p.medium_score != null || p.hard_score != null) return 'in_progress';
    if (p.basic_score != null && p.basic_score < 50) return 'needs_practice';
    if (p.basic_score != null) return 'in_progress';
    return 'not_started';
  };

  const getBestScore = (chapter) => {
    const p = chapter.student_progress;
    const scores = [p.basic_score, p.medium_score, p.hard_score].filter(s => s != null);
    return scores.length > 0 ? Math.max(...scores) : null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTest = async (chapter, difficulty) => {
    if (!chapter.has_questions) {
      toast.error('No questions available yet. Please ask your teacher to generate questions.');
      return;
    }
    if (!canAttemptDifficulty(chapter, difficulty)) {
      toast.error('Complete the previous level first to unlock this one.');
      return;
    }
    setTestLoading(true);
    setSelectedChapter(chapter);
    setTestDifficulty(difficulty);
    try {
      const response = await axios.get(`${API}/skill-assessment/test/${chapter.id}/${difficulty}`);
      setQuestions(response.data.questions);
      setTimeLeft(response.data.time_limit_minutes * 60);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setView('test');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start test');
    } finally {
      setTestLoading(false);
    }
  };

  const selectAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const submitTest = async () => {
    setTestLoading(true);
    try {
      const response = await axios.post(
        `${API}/skill-assessment/submit/${selectedChapter.id}/${testDifficulty}`,
        { answers }
      );
      setTestResults(response.data);
      setView('results');
      loadChapters();
    } catch (error) {
      toast.error('Failed to submit test');
    } finally {
      setTestLoading(false);
    }
  };

  const goBackToChapters = () => {
    setView('chapters');
    setTestResults(null);
    setSelectedChapter(null);
    setQuestions([]);
    setAnswers({});
    setTimeLeft(null);
  };

  // ===== RESULTS VIEW =====
  if (view === 'results' && testResults) {
    const passed = testResults.score >= 70;
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shadow-lg">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={goBackToChapters} className="flex items-center gap-2 text-white hover:text-teal-200 transition-colors" data-testid="back-to-chapters-btn">
              <ArrowLeft size={20} />
              <span className="font-medium">Back to Chapters</span>
            </button>
            <Badge className="bg-white/20 text-white border-white/30">{selectedChapter?.name}</Badge>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-teal-100">
            <div className={`p-8 text-center text-white ${
              testResults.score >= 90 ? 'bg-gradient-to-r from-green-500 to-teal-500' :
              testResults.score >= 50 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-red-500 to-pink-500'
            }`}>
              <div className="w-28 h-28 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4 shadow-lg">
                <span className="text-5xl font-bold" data-testid="score-display">{testResults.score}%</span>
              </div>
              <h1 className="text-2xl font-bold">
                {testResults.score >= 90 ? 'Excellent!' : testResults.score >= 70 ? 'Well Done!' : testResults.score >= 50 ? 'Good Effort!' : 'Keep Practicing!'}
              </h1>
              <p className="opacity-80 mt-2">{selectedChapter?.name} - {testDifficulty?.charAt(0).toUpperCase() + testDifficulty?.slice(1)}</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                  <CheckCircle className="mx-auto text-green-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-green-600" data-testid="correct-count">{testResults.correct}</p>
                  <p className="text-sm text-gray-500">Correct</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl border border-red-100">
                  <AlertTriangle className="mx-auto text-red-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-red-600" data-testid="wrong-count">{testResults.total - testResults.correct}</p>
                  <p className="text-sm text-gray-500">Wrong</p>
                </div>
                <div className="text-center p-4 bg-teal-50 rounded-xl border border-teal-100">
                  <Target className="mx-auto text-teal-500 mb-2" size={28} />
                  <p className="text-2xl font-bold text-teal-600" data-testid="total-count">{testResults.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
              </div>

              {testResults.expert_unlocked && (
                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl text-center">
                  <Award className="mx-auto text-purple-500 mb-2" size={28} />
                  <p className="font-bold text-purple-700">Expert Level Unlocked!</p>
                </div>
              )}

              {testResults.next_steps && testResults.next_steps.length > 0 && (
                <div className="mb-6 p-4 rounded-xl bg-teal-50 border border-teal-200">
                  <h3 className="font-bold text-teal-800 mb-2">Next Steps</h3>
                  {testResults.next_steps.map((step, i) => (
                    <p key={i} className="text-sm text-teal-700 flex items-start gap-2 mb-1">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" /> {step}
                    </p>
                  ))}
                </div>
              )}

              {testResults.weak_areas && testResults.weak_areas.length > 0 && (
                <div className="mb-6 p-4 rounded-xl bg-orange-50 border border-orange-200">
                  <h3 className="font-bold text-orange-800 mb-2">Areas to Improve</h3>
                  {testResults.weak_areas.map((area, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border border-orange-200 mb-3 last:mb-0">
                      <div className="flex items-start gap-2 mb-2">
                        <p className="text-gray-800 text-sm font-semibold flex-1">Q{i + 1}. {area.question}</p>
                        {area.type && area.type !== 'mcq' && (
                          <Badge className="bg-orange-100 text-orange-600 text-xs shrink-0">{area.type === 'short_answer' ? '1 Mark' : '2 Marks'}</Badge>
                        )}
                      </div>

                      {/* MCQ: show all options highlighting student's pick and correct */}
                      {area.type === 'mcq' && area.options && (
                        <div className="space-y-1 mb-2">
                          {Object.entries(area.options).map(([letter, text]) => {
                            const isCorrect = letter === area.correct_answer;
                            const isStudent = letter === area.student_answer;
                            const cls = isCorrect
                              ? 'bg-green-50 border-green-300 text-green-900'
                              : isStudent
                                ? 'bg-red-50 border-red-300 text-red-900'
                                : 'bg-gray-50 border-gray-200 text-gray-700';
                            return (
                              <div key={letter} className={`flex items-start gap-2 p-2 rounded border text-xs ${cls}`}>
                                <span className="font-semibold">{letter}.</span>
                                <span className="flex-1">{text}</span>
                                {isCorrect && <span className="text-green-700 font-medium shrink-0">✓ Correct</span>}
                                {isStudent && !isCorrect && <span className="text-red-700 font-medium shrink-0">✗ Your answer</span>}
                              </div>
                            );
                          })}
                          {!area.student_answer && (
                            <p className="text-xs text-amber-700 italic">You did not answer this question.</p>
                          )}
                        </div>
                      )}

                      {/* Written answer: show student's text + model answer */}
                      {area.type !== 'mcq' && (
                        <div className="space-y-2 mb-2">
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">Your answer{typeof area.marks_earned === 'number' && ` (${area.marks_earned}/${area.max_marks ?? '-'} marks)`}:</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{area.student_answer || <em className="text-gray-400">Not answered</em>}</p>
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-xs font-semibold text-green-700 mb-1">Model answer:</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{area.correct_answer}</p>
                          </div>
                        </div>
                      )}

                      {/* MCQ summary line (in case options dict is missing) */}
                      {area.type === 'mcq' && !area.options && (
                        <p className="text-xs text-green-700 mb-1"><CheckCircle size={12} className="inline mr-1" />Correct: {area.correct_answer}</p>
                      )}

                      {/* Explanation (always render if present) */}
                      {area.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                          <p className="text-xs font-semibold text-blue-700 mb-0.5">Why:</p>
                          <p className="text-xs text-blue-900">{area.explanation}</p>
                        </div>
                      )}

                      {/* AI feedback (for written) */}
                      {area.feedback && (
                        <p className="text-xs text-teal-700 mt-2 bg-teal-50 p-2 rounded border border-teal-200">
                          <PenLine size={12} className="inline mr-1" />
                          <strong>AI Feedback:</strong> {area.feedback}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={goBackToChapters} variant="outline" className="flex-1 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="all-chapters-btn">
                  <Home className="mr-2" size={18} /> All Chapters
                </Button>
                {testResults.can_progress && testDifficulty !== 'hard' && (
                  <Button
                    onClick={() => {
                      const next = { basic: 'medium', medium: 'hard' }[testDifficulty];
                      if (next) { setTestResults(null); startTest(selectedChapter, next); }
                    }}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 shadow-md"
                    data-testid="next-level-btn"
                  >
                    <TrendingUp className="mr-2" size={18} /> Next Level
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== TEST VIEW =====
  if (view === 'test') {
    if (testLoading || !questions.length) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-3" />
            <p className="text-teal-700 font-medium">Loading questions...</p>
          </div>
        </div>
      );
    }

    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-4 py-4 sticky top-0 z-10 shadow-lg">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <button onClick={goBackToChapters} className="flex items-center gap-2 text-white hover:text-teal-200 transition-colors" data-testid="exit-test-btn">
                <ArrowLeft size={20} />
                <span className="font-medium">Exit Test</span>
              </button>
              <div className="flex items-center gap-3">
                <Badge className="bg-white/20 text-white border-white/30">{selectedChapter?.name}</Badge>
                <span className="text-sm font-bold bg-white/20 px-3 py-1 rounded-full">
                  {currentQuestionIndex + 1} / {questions.length}
                </span>
                {timeLeft !== null && (
                  <span className={`text-sm font-bold px-3 py-1 rounded-full ${timeLeft < 60 ? 'bg-red-500' : 'bg-white/20'}`}>
                    <Clock size={14} className="inline mr-1" />{formatTime(timeLeft)}
                  </span>
                )}
              </div>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4 border border-teal-100">
            <div className="flex gap-2 mb-4">
              <Badge className="bg-teal-100 text-teal-700 font-semibold">{testDifficulty}</Badge>
              {currentQuestion?.type === 'short_answer' && (
                <Badge className="bg-orange-100 text-orange-700 font-semibold"><PenLine size={12} className="mr-1" />1 Mark - Short Answer</Badge>
              )}
              {currentQuestion?.type === 'descriptive' && (
                <Badge className="bg-purple-100 text-purple-700 font-semibold"><PenLine size={12} className="mr-1" />2 Marks - Descriptive</Badge>
              )}
              {currentQuestion?.type === 'mcq' && (
                <Badge className="bg-gray-100 text-gray-600 font-semibold">MCQ</Badge>
              )}
              {currentQuestion?.is_important && (
                <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 font-semibold" title={currentQuestion?.importance_reason || 'Frequently appears in board exams'} data-testid="important-question-badge">
                  ⭐ Important
                </Badge>
              )}
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-6" data-testid="question-text">
              {currentQuestion?.question}
            </h2>

            {/* MCQ Options */}
            {(currentQuestion?.type === 'mcq' || !currentQuestion?.type) && currentQuestion?.options && (
              <div className="space-y-3">
                {Object.entries(currentQuestion.options).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => selectAnswer(currentQuestion.id, key)}
                    data-testid={`option-${key}`}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers[currentQuestion?.id] === key
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                        answers[currentQuestion?.id] === key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>{key}</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Short Answer (1 mark) */}
            {currentQuestion?.type === 'short_answer' && (
              <div className="space-y-3">
                <Textarea
                  value={answers[currentQuestion?.id] || ''}
                  onChange={(e) => selectAnswer(currentQuestion.id, e.target.value)}
                  placeholder="Write your answer here (1-2 sentences)..."
                  className="min-h-[80px] border-2 border-gray-200 focus:border-teal-500 rounded-xl p-4 text-base"
                  data-testid="short-answer-input"
                />
                <p className="text-xs text-gray-400">Write a brief answer in 1-2 sentences</p>
              </div>
            )}

            {/* Descriptive Answer (2 marks) */}
            {currentQuestion?.type === 'descriptive' && (
              <div className="space-y-3">
                <Textarea
                  value={answers[currentQuestion?.id] || ''}
                  onChange={(e) => selectAnswer(currentQuestion.id, e.target.value)}
                  placeholder="Write your detailed answer here (3-5 sentences)..."
                  className="min-h-[140px] border-2 border-gray-200 focus:border-teal-500 rounded-xl p-4 text-base"
                  data-testid="descriptive-answer-input"
                />
                <p className="text-xs text-gray-400">Write a detailed answer in 3-5 sentences for full marks</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {currentQuestionIndex > 0 && (
              <Button variant="outline" onClick={() => setCurrentQuestionIndex(prev => prev - 1)} className="flex-1 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="prev-btn">
                <ChevronLeft className="mr-1" size={18} /> Previous
              </Button>
            )}
            {currentQuestionIndex < questions.length - 1 ? (
              <Button onClick={() => setCurrentQuestionIndex(prev => prev + 1)} className="flex-1 bg-teal-600 hover:bg-teal-700 shadow-md" data-testid="next-btn">
                Next <ChevronRight className="ml-1" size={18} />
              </Button>
            ) : (
              <Button onClick={submitTest} className="flex-1 bg-teal-600 hover:bg-teal-700 shadow-md" disabled={testLoading} data-testid="submit-test-btn">
                {testLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="mr-2" size={18} />}
                Submit Test
              </Button>
            )}
          </div>

          <div className="mt-4 bg-white rounded-2xl shadow-lg p-4 border border-teal-100">
            <p className="text-sm text-teal-600 font-medium mb-3">Jump to question:</p>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestionIndex(index)}
                  data-testid={`jump-q-${index + 1}`}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                    currentQuestionIndex === index
                      ? 'bg-teal-600 text-white shadow-md'
                      : answers[q.id]
                        ? 'bg-teal-100 text-teal-700 border border-teal-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-teal-50'
                  }`}
                >{index + 1}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== CHAPTERS VIEW =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-teal-600 rounded-lg transition-colors" data-testid="back-to-dashboard-btn">
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Topic Tests</h1>
              <p className="text-teal-200 text-sm">Climb from Basic to Expert — chapter by chapter</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-white/30">Class {studentClass}</Badge>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Subject Tabs */}
        <div className="bg-white rounded-xl shadow-md p-1 mb-6 flex gap-1 border border-teal-100">
          {subjects.map(subject => (
            <button
              key={subject}
              onClick={() => setSelectedSubject(subject)}
              data-testid={`subject-tab-${subject.toLowerCase()}`}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                selectedSubject === subject ? 'bg-teal-600 text-white shadow-md' : 'text-gray-600 hover:bg-teal-50'
              }`}
            >{subject}</button>
          ))}
        </div>

        {/* Adaptive Info Banner */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-5 mb-6 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Brain size={28} />
            </div>
            <div>
              <h2 className="text-lg font-bold">Adaptive Learning</h2>
              <p className="text-teal-100 text-sm">Score 50%+ to unlock the next difficulty level. Master all levels to become a champion!</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            <span className="ml-3 text-gray-600">Loading chapters...</span>
          </div>
        ) : chapters.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Chapters Available Yet</h3>
            <p className="text-yellow-600">{noChaptersMessage}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {chapters.map((chapter, index) => {
              const status = getChapterStatus(chapter);
              const bestScore = getBestScore(chapter);
              const p = chapter.student_progress;

              return (
                <div key={chapter.id} data-testid={`chapter-card-${chapter.id}`} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-teal-100">
                  {/* Colored Header */}
                  <div className={`px-5 py-4 text-white ${
                    status === 'mastered' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                    status === 'in_progress' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                    status === 'needs_practice' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                    'bg-gradient-to-r from-teal-500 to-teal-600'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Chapter {index + 1}</p>
                        <h3 className="font-bold">{chapter.name}</h3>
                        <p className="text-xs opacity-70 mt-1">{chapter.topics_covered} of {chapter.total_topics} topics covered</p>
                      </div>
                      {bestScore !== null && (
                        <div className="text-right">
                          <p className="text-2xl font-bold">{bestScore}%</p>
                          <p className="text-xs opacity-80">Best Score</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5">
                    {/* Per-level scores */}
                    {(p.basic_score != null || p.medium_score != null || p.hard_score != null || p.expert_score != null) && (
                      <div className="flex flex-wrap gap-2 mb-4 text-xs">
                        {p.basic_score != null && <Badge className="bg-green-50 text-green-600 border border-green-200"><CheckCircle size={10} className="mr-1" />Basic: {p.basic_score}%</Badge>}
                        {p.medium_score != null && <Badge className="bg-yellow-50 text-yellow-600 border border-yellow-200"><CheckCircle size={10} className="mr-1" />Intermediate: {p.medium_score}%</Badge>}
                        {p.hard_score != null && <Badge className="bg-orange-50 text-orange-600 border border-orange-200"><CheckCircle size={10} className="mr-1" />Advanced: {p.hard_score}%</Badge>}
                      </div>
                    )}

                    {!chapter.has_questions ? (
                      chapter.generation_status === 'generating' ? (
                        <div className="bg-blue-50 text-blue-600 text-sm px-3 py-2 rounded-lg text-center flex items-center justify-center gap-2" data-testid={`chapter-generating-student-${chapter.id}`}>
                          <Loader2 size={14} className="animate-spin" />
                          Questions being generated... Please wait
                        </div>
                      ) : (
                        <div className="bg-orange-50 text-orange-600 text-sm px-3 py-2 rounded-lg text-center" data-testid={`chapter-no-questions-${chapter.id}`}>
                          Questions not yet generated - Ask your teacher
                        </div>
                      )
                    ) : (
                      <>
                        {/* Difficulty Buttons */}
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { key: 'basic', label: 'Basic', color: 'bg-green-500 hover:bg-green-600', ring: 'ring-green-300' },
                            { key: 'medium', label: 'Intermediate', color: 'bg-yellow-500 hover:bg-yellow-600', ring: 'ring-yellow-300' },
                            { key: 'hard', label: 'Advanced', color: 'bg-red-500 hover:bg-red-600', ring: 'ring-red-300' },
                          ].map(({ key, label, color, ring }) => {
                            const unlocked = canAttemptDifficulty(chapter, key);
                            const completed = p[`${key}_score`] != null;
                            return (
                              <Button
                                key={key}
                                size="sm"
                                onClick={() => startTest(chapter, key)}
                                disabled={testLoading || !unlocked}
                                data-testid={`${key}-btn-${chapter.id}`}
                                className={unlocked ? `${color} text-white ${completed ? `ring-2 ${ring}` : ''}` : 'bg-gray-300 text-gray-500 cursor-not-allowed'}
                              >
                                {!unlocked && <Lock size={12} className="mr-1" />}
                                {completed && <Star size={12} className="mr-1" />}
                                {label}
                              </Button>
                            );
                          })}
                        </div>

                        {/* Unlock hint */}
                        {!canAttemptDifficulty(chapter, 'medium') && p.basic_score === null && (
                          <p className="text-xs text-center text-teal-600 mt-3 font-medium">Start with Basic to begin!</p>
                        )}
                        {p.basic_score !== null && p.basic_score < 50 && (
                          <p className="text-xs text-center text-orange-600 mt-3 font-medium">Score 50%+ on Basic to unlock Intermediate</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillAssessment;
